import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import subscriptionService, { Plan } from '../../services/subscriptionService';

interface SubscriptionWallProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  allowClose?: boolean;
}

const SubscriptionWall: React.FC<SubscriptionWallProps> = ({
  isOpen,
  onClose,
  message = 'Your subscription has expired. Please upgrade to continue using this feature.',
  allowClose = false,
}) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen]);

  const fetchPlans = async () => {
    try {
      const availablePlans = await subscriptionService.getAvailablePlans();
      setPlans(availablePlans.filter((p) => p.plan !== 'trial'));
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = (plan: string) => {
    navigate(`/subscription/upgrade?plan=${plan}`);
    if (allowClose) {
      onClose();
    }
  };

  const handleManageSubscription = () => {
    navigate('/subscription');
    if (allowClose) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 relative">
          {allowClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white bg-opacity-20 rounded-full p-4">
              <LockClosedIcon className="h-12 w-12 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white text-center mb-2">
            Subscription Required
          </h2>
          <p className="text-white text-center opacity-90">{message}</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading plans...</p>
            </div>
          ) : (
            <>
              {/* Plans Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {plans.map((plan) => {
                  const isPopular = plan.plan === 'professional';
                  return (
                    <div
                      key={plan.plan}
                      className={`relative rounded-xl border-2 p-6 ${
                        isPopular
                          ? 'border-indigo-600 shadow-lg scale-105'
                          : 'border-gray-200'
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-indigo-600 text-white px-4 py-1 rounded-full text-xs font-semibold">
                            MOST POPULAR
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-4">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {subscriptionService.formatPlanName(plan.plan)}
                        </h3>
                        <div className="mb-2">
                          <span className="text-4xl font-bold text-gray-900">
                            {subscriptionService.formatCurrency(plan.pricing.monthly)}
                          </span>
                          <span className="text-gray-600">/month</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          or {subscriptionService.formatCurrency(plan.pricing.annual)}/year
                          <span className="block text-xs text-green-600 font-medium">
                            Save {Math.round(((plan.pricing.monthly * 12 - plan.pricing.annual) / (plan.pricing.monthly * 12)) * 100)}%
                          </span>
                        </p>
                      </div>

                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start text-sm">
                          <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>
                            <strong>{plan.limits.maxUsers === -1 ? 'Unlimited' : plan.limits.maxUsers}</strong> Users
                          </span>
                        </li>
                        <li className="flex items-start text-sm">
                          <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>
                            <strong>{plan.limits.maxWarehouses === -1 ? 'Unlimited' : plan.limits.maxWarehouses}</strong> Warehouses
                          </span>
                        </li>
                        <li className="flex items-start text-sm">
                          <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>
                            <strong>{plan.limits.maxInventoryItems === -1 ? 'Unlimited' : plan.limits.maxInventoryItems}</strong> Inventory Items
                          </span>
                        </li>
                        <li className="flex items-start text-sm">
                          <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>
                            <strong>Unlimited</strong> Purchase Orders
                          </span>
                        </li>
                        <li className="flex items-start text-sm">
                          <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>
                            <strong>Unlimited</strong> Retail Bills
                          </span>
                        </li>
                        {plan.features.slice(0, 3).map((feature) => (
                          <li key={feature} className="flex items-start text-sm text-gray-600">
                            <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>{subscriptionService.formatFeatureName(feature)}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleUpgrade(plan.plan)}
                        className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                          isPopular
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200 border border-gray-300'
                        }`}
                      >
                        Select {subscriptionService.formatPlanName(plan.plan)}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Footer Actions */}
              <div className="border-t pt-6">
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    onClick={handleManageSubscription}
                    className="px-6 py-2 text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    View Current Subscription
                  </button>
                  {allowClose && (
                    <button
                      onClick={onClose}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Close
                    </button>
                  )}
                </div>
                <p className="text-center text-xs text-gray-500 mt-4">
                  Need help choosing? Contact our sales team at sales@example.com
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionWall;
